import { Check } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui';
import { cn } from '@/lib/utils';
import type { PlacementOption } from '../types';

interface PlacementModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'front' | 'back';
  placements: PlacementOption[];
  selectedPlacement: string;
  onSelect: (placement: string) => void;
  fileName: string;
}

/**
 * Dialog for choosing the placement position of an uploaded design.
 * Selecting an option updates the wizard data at once; "Confirm" and
 * "Cancel" only close the dialog.
 */
export function PlacementModal({
  isOpen,
  onClose,
  type,
  placements,
  selectedPlacement,
  onSelect,
  fileName,
}: PlacementModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent closeLabel="Close">
        <DialogTitle>
          {type === 'front' ? 'Choose Front Position' : 'Choose Back Position'}
        </DialogTitle>
        <DialogDescription>
          Where should <span className="font-medium text-ink">{fileName}</span>{' '}
          be placed?
        </DialogDescription>

        <div className="space-y-2" role="group" aria-label="Position">
          {placements.map((p) => {
            const selected = selectedPlacement === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onSelect(p.id)}
                aria-pressed={selected}
                className={cn(
                  'flex w-full items-center gap-4 rounded-card bg-paper p-4 text-left transition-[background-color,box-shadow] duration-[120ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  selected
                    ? 'ring-2 ring-primary'
                    : 'ring-1 ring-line hover:bg-paper-3'
                )}
              >
                <span
                  className={cn(
                    'flex h-12 w-12 shrink-0 items-center justify-center rounded-card text-xl',
                    selected
                      ? 'bg-primary-soft'
                      : 'bg-paper-2 ring-1 ring-inset ring-line'
                  )}
                  aria-hidden="true"
                >
                  {p.icon}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-ink">
                    {p.label}
                  </span>
                  <span className="block text-sm text-ink-2">
                    {p.description}
                  </span>
                </span>
                {selected && (
                  <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-pill bg-primary text-primary-fg"
                    aria-hidden="true"
                  >
                    <Check className="h-4 w-4" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onClose}
            disabled={!selectedPlacement}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
